import { ErrorHandler, Injectable, inject } from '@angular/core';

import {
  Observable,
  map,
  merge,
  catchError,
  filter,
  forkJoin,
  tap,
  combineLatest,
  take,
  switchMap,
  startWith,
  pairwise,
} from 'rxjs';

import { AccountUser, LocationUser, User } from '@shared/user/models';
import { Account } from '@shared/account/models';
import { Location } from '@shared/location/models';
import { ResourceSettings, ServiceSettings } from '@shared/restaurant/models';
import { AuthUser } from '@shared/signup/models';
import { makeMap } from '@shared/core';
import { TokenService } from '@tablesready/frontend-auth';
import { AuthUserService } from './auth-user.service';
import { AuthAccountService } from './auth-account.service';
import { AuthLocationService } from './auth-location.service';
import { AuthAccountUserService } from './auth-account-user.service';
import { AuthLocationUserService } from './auth-location-user.service';
import { AuthRestaurantQuery, AuthRestaurantStore } from '../states';

// TODO: rename
@Injectable({providedIn: 'root'})
export class AuthRestaurantService {
  private accountsMap: Record<string, Account> = {};
  private locationsMap: Record<string, Location> = {};
  private accountUsersMap: Record<string, AccountUser> = {};
  private locationUsersMap: Record<string, LocationUser> = {};
  private readonly errorHandler = inject(ErrorHandler);

  constructor(
    private readonly userService: AuthUserService,
    private readonly accountService: AuthAccountService,
    private readonly locationService: AuthLocationService,
    private readonly accountUserService: AuthAccountUserService,
    private readonly locationUserService: AuthLocationUserService,
    private readonly query: AuthRestaurantQuery,
    private readonly store: AuthRestaurantStore,
    private readonly tokenService: TokenService,
  ) {
  }

  select(): Observable<AuthUser> {
    return this.query.select();
  }

  selectWithAccount(): Observable<AuthUser> {
    return this.query.select()
      .pipe(filter(user => user.account !== undefined));
  }

  selectWithAccountAndLocation(): Observable<AuthUser> {
    return this.query.select()
      .pipe(filter(user => user.account !== undefined && user.location !== undefined));
  }

  /**
   * Use it only in cases you need to detect location changes. Don't use if you need keeping actual location data.
   */
  selectAndDetectLocationChanges(): Observable<AuthUser> {
    return this.query.select()
      .pipe(
        startWith(null),
        pairwise(),
        filter(([prev, current]) => prev === null || prev.location?._id !== current.location?._id && !!current.location?._id),
        map(([, current]) => current),
      );
  }

  selectAndDetectAccountChanges(): Observable<AuthUser> {
    return this.query.select()
      .pipe(
        startWith(null),
        pairwise(),
        filter(([prev, current]) => prev === null || prev.account?.id !== current.account?.id && !!current.account?.id),
        map(([, current]) => current),
      );
  }

  selectAndDetectGoogleIntegrationAvailability(): Observable<AuthUser> {
    return this.query.select()
      .pipe(
        startWith(null),
        pairwise(),
        filter(([prev, current]) => prev === null || prev.location?.integrations?.reserveWithGoogle?.enabled !== current.location?.integrations?.reserveWithGoogle?.enabled),
        map(([, current]) => current),
      );
  }

  get(): AuthUser {
    return this.query.getValue();
  }

  load(): Observable<[User, Account[], Location[], AccountUser[], LocationUser[]]> {
    return forkJoin([
      this.userService.load(),
      this.accountService.load(),
      this.locationService.load(),
      this.accountUserService.load(),
      this.locationUserService.load(),
    ]);
  }

  // Listens websockets events on children services
  listenToChanges() {
    return this.userService.select()
      .pipe(
        switchMap(user => {
          return merge(
            this.userService.listenToChanges(),
            this.accountService.listenToChanges(),
            this.locationService.listenToChanges(),
            this.accountUserService.listenToChanges(user),
            this.locationUserService.listenToChanges(user),
          )
            // Skip an event whose handler throws instead of ending the stream: the app-wide
            // subscriber also runs build(), so the auth user would freeze until a reload.
            .pipe(catchError((error: unknown, handlers$) => {
              this.errorHandler.handleError(error);
              return handlers$;
            }));
        }),
      );
  }

  // Listens to changes of children services and builds AuthUser
  build() {
    return combineLatest([
      this.userService.select(),
      this.accountService.selectAll()
        .pipe(tap(accounts => this.accountsMap = makeMap(accounts, 'id'))),
      this.locationService.selectAllEnabled()
        .pipe(tap(locations => this.locationsMap = makeMap(locations))),
      this.accountUserService.selectAll()
        .pipe(tap(accountUsers => this.accountUsersMap = makeMap(accountUsers, 'accountId'))),
      this.locationUserService.selectAll()
        .pipe(tap(locationUsers => this.locationUsersMap = makeMap(locationUsers, 'locationId'))),
    ])
      .pipe(
        map(([user, , locations]): AuthUser => {
          if (!this.tokenService.getActive()?.locationId) {
            return user;
          }

          let location = this.locationsMap[this.tokenService.getActive()?.locationId];
          if (!location) {
            location = locations[0];
            if (!location) {
              return user;
            }
          }

          const locationUser = this.locationUsersMap[location._id];
          if (!locationUser) {
            return user;
          }

          const account = this.accountsMap[location.accountId];
          if (!account) {
            return user;
          }

          const accountUser = this.accountUsersMap[account.id];
          if (!accountUser) {
            return user;
          }

          return {
            ...user,
            account,
            location,
            accountUser,
            locationUser,
          };
        }),
        filter(user => !!user),
        tap(user => this.store._setState(user)),
      );
  }

  buildOnce(): Observable<AuthUser> {
    return this.build().pipe(take(1));
  }

  serviceSettings(): Observable<ServiceSettings> {
    return this.select()
      .pipe(
        filter(user => !!user.location),
        map(user => user.location.serviceSettings),
      );
  }

  resourceSettings(): Observable<ResourceSettings> {
    return this.select()
      .pipe(
        filter(user => !!user.location),
        map(user => user.location.resourceSettings),
      );
  }

  reset() {
    this.userService.reset();
    this.accountService.reset();
    this.locationService.reset();
    this.accountUserService.reset();
    this.locationUserService.reset();
  }
}
